import type { ValidTokenResponse } from './responses';

// ── API 베이스 URL 관리 ───────────────────────────────────────────
// 각 마이크로서비스별 베이스 URL을 환경 변수로부터 주입받습니다.
// 환경 변수가 없을 경우 Vite 프록시 설정을 활용한 상대 경로를 폴백으로 사용합니다.
export const API_BASE = {
  pst:      import.meta.env.VITE_API_PST_URL      ?? '/api/app/pst',
  // pst:      import.meta.env.VITE_API_PST_URL      ?? 'http://localhost:9589/pst/app',
  // pst:      import.meta.env.VITE_API_PST_URL      ?? 'https://tsummitplay.hakine.com/api/app/pst',
  user:     import.meta.env.VITE_API_USER_URL     ?? '/api/user',
  item:     import.meta.env.VITE_API_ITEM_URL     ?? '/api/item',
  contest:  import.meta.env.VITE_API_CONTEST_URL  ?? '/api/contest',
  schedule: import.meta.env.VITE_API_SCHEDULE_URL ?? '/api/schedule',
} as const;

/** API_BASE 의 키 타입 ('pst' | 'user' | 'item' | 'contest' | 'schedule') */
export type ApiBase = keyof typeof API_BASE;

// ── 토큰 관리 ─────────────────────────────────────────────────────
// 인메모리 액세스 토큰 / 리프레시 토큰 저장소입니다.
// 로그인 성공 시 set(), 로그아웃 시 clear() 를 호출하세요.
let _accessToken:  string | null = null;
let _refreshToken: string | null = null;

export const tokenStore = {
  /** 현재 저장된 액세스 토큰을 반환합니다. */
  getAccessToken:  () => _accessToken,
  /** 현재 저장된 리프레시 토큰을 반환합니다. */
  getRefreshToken: () => _refreshToken,
  /** 액세스 토큰과 리프레시 토큰을 함께 저장합니다. */
  set: (accessToken: string, refreshToken: string) => {
    _accessToken  = accessToken;
    _refreshToken = refreshToken;
  },
  /** 액세스 토큰과 리프레시 토큰을 모두 초기화합니다. */
  clear: () => {
    _accessToken  = null;
    _refreshToken = null;
  },
};

// ── 타입 ──────────────────────────────────────────────────────────
/**
 * HTTP 요청 옵션 타입.
 * 표준 RequestInit 에서 body/method 를 제외하고 확장합니다.
 */
export interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  /** 요청 바디 (자동으로 JSON.stringify) */
  body?: unknown;
  /** query string 파라미터 — undefined/null 값은 자동으로 제외됩니다. */
  params?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * HTTP fetch 래퍼 응답 타입.
 * @template T - 응답 data 의 실제 타입
 */
export interface ApiResponse<T = unknown> {
  /** HTTP 응답이 성공(2xx)인지 여부 */
  ok:     boolean;
  /** HTTP 상태 코드 */
  status: number;
  /** 파싱된 응답 데이터 */
  data:   T;
}

// ── 내부 헬퍼 ─────────────────────────────────────────────────────
/**
 * 베이스 URL + 경로 + 쿼리 파라미터를 조합하여 최종 URL 문자열을 반환합니다.
 *
 * - base 가 'http(s)://' 로 시작하면 절대 URL을 그대로 반환합니다.
 * - 상대 경로인 경우 pathname + search 만 반환하여 현재 오리진에 종속되지 않도록 합니다.
 *
 * @param base   - 서비스 베이스 URL (절대/상대 모두 가능)
 * @param path   - 엔드포인트 경로 (예: '/users/1')
 * @param params - 추가할 쿼리 파라미터 맵
 */
function buildUrl(base: string, path: string, params?: RequestOptions['params']): string {
  // URL 생성 시 상대 경로도 파싱 가능하도록 window.location.origin 을 베이스로 활용합니다.
  const url = new URL(`${base}${path}`, window.location.origin);

  if (params) {
    // undefined/null 값은 쿼리스트링에서 제외합니다.
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        url.searchParams.set(k, String(v));
      }
    });
  }

  // 절대 URL이면 전체 문자열 반환, 상대 경로면 pathname + search 만 반환합니다.
  return base.startsWith('http') ? url.toString() : `${url.pathname}${url.search}`;
}

/**
 * 공통 요청 헤더를 생성합니다.
 * Content-Type 은 항상 'application/json' 으로 설정되며,
 * 토큰이 존재하면 Authorization 헤더를 Bearer 방식으로 추가합니다.
 *
 * @param extra - 추가로 병합할 헤더 (Content-Type 을 덮어쓸 수 있습니다)
 */
function buildHeaders(extra?: HeadersInit): Headers {
  const headers = new Headers({ 'Content-Type': 'application/json', ...extra });

  // tokenStore 에 토큰이 있으면 Bearer 인증 헤더를 추가합니다.
  const token = tokenStore.getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
}

// ── 공통 fetch 래퍼 ───────────────────────────────────────────────
/**
 * 내부 fetch 래퍼 함수. 모든 HTTP 메서드 함수(get/post/put/patch/del)가 이를 통해 요청합니다.
 *
 * - 응답 본문을 우선 텍스트로 읽은 뒤 JSON 파싱을 시도합니다.
 * - JSON 파싱 실패 시 텍스트 그대로 data 에 할당합니다.
 * - HTTP 오류(4xx, 5xx)가 발생하면 ApiError 를 throw 합니다.
 *
 * @template T  - 응답 data 의 타입
 * @param method  - HTTP 메서드 문자열 ('GET', 'POST', ...)
 * @param base    - 서비스 베이스 URL
 * @param path    - 엔드포인트 경로
 * @param options - 추가 요청 옵션 (body, params, headers 등)
 * @throws {ApiError} HTTP 오류 상태 코드일 때
 */
async function request<T = unknown>(
  method:  string,
  base:    string,
  path:    string,
  options: RequestOptions = {},
): Promise<ApiResponse<T>> {
  const { body, params, headers: extraHeaders, ...rest } = options;

  // 토큰이 존재하면 validToken을 호출하여 토큰을 갱신합니다.
  // console.log('request: tokenStore.getAccessToken() called');
  // console.log(`request: current token: ${tokenStore.getAccessToken()}`);

  if (tokenStore.getAccessToken()) {
    try {
      await validToken();
    } catch (e) {
      // 토큰 갱신 실패 시 기존 토큰을 초기화하고 에러를 전파합니다.
      tokenStore.clear();
      throw e;
    }
  }

  const url     = buildUrl(base, path, params);
  const headers = buildHeaders(extraHeaders as HeadersInit);

  // fetch 초기화 객체를 구성합니다. body 가 있을 때만 JSON 직렬화하여 포함합니다.
  const init: RequestInit = {
    method,
    headers,
    ...rest,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  const res  = await fetch(url, init);
  // 응답을 먼저 텍스트로 수신합니다 (빈 응답 처리를 위해).
  const text = await res.text();

  let data: T;
  try {
    // 텍스트가 있으면 JSON 파싱, 없으면 undefined 로 처리합니다.
    data = text ? (JSON.parse(text) as T) : (undefined as T);
  } catch {
    // JSON 파싱 실패 시 원본 텍스트를 그대로 반환합니다.
    data = text as unknown as T;
  }

  // 2xx 이외의 상태 코드는 ApiError 로 변환하여 throw 합니다.
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText, data);
  }

  return { ok: res.ok, status: res.status, data };
}

// ── ApiError ──────────────────────────────────────────────────────
/**
 * HTTP 오류 응답을 나타내는 커스텀 에러 클래스.
 * `request()` 내부에서 응답 상태가 2xx 가 아닐 때 throw 됩니다.
 *
 * @template T - 오류 응답 data 의 타입
 */
export class ApiError<T = unknown> extends Error {
  constructor(
    /** HTTP 상태 코드 (예: 404, 500) */
    public readonly status:     number,
    /** HTTP 상태 메시지 (예: 'Not Found') */
    public readonly statusText: string,
    /** 파싱된 오류 응답 데이터 */
    public readonly data:       T,
  ) {
    super(`[${status}] ${statusText}`);
    this.name = 'ApiError';
  }
}

// ── 토큰 갱신 ─────────────────────────────────────────────────────
/**
 * 서버에 현재 토큰의 유효성을 검증하고 새 토큰으로 갱신합니다.
 *
 * ## 동작 흐름
 * 1. 액세스 토큰이 있으면 Authorization 헤더에 Bearer 로 포함합니다.
 * 2. body 에 `accTkn`(액세스 토큰), `rfrTkn`(리프레시 토큰) 을 담아 POST 합니다.
 * 3. 응답의 `code` 가 0 이면 성공 — `data.accTkn`, `data.rfrTkn` 을 tokenStore 에 저장합니다.
 * 4. `code` 가 0 이 아니면 오류로 간주하여 `TokenRefreshError` 를 throw 합니다.
 *
 * @throws {TokenRefreshError} 서버 응답 code 가 0 이 아닐 때
 */
export class TokenRefreshError extends Error {
  constructor(
    /** 서버가 반환한 비즈니스 오류 코드 */
    public readonly code:    number,
    /** 서버가 반환한 오류 메시지 */
    public readonly message: string,
  ) {
    super(`[validToken] code=${code} ${message}`);
    this.name = 'TokenRefreshError';
  }
}

/**
 * pst 서비스의 `/validToken` 엔드포인트로 토큰 갱신 요청을 보냅니다.
 *
 * @returns 갱신된 액세스 토큰과 리프레시 토큰
 * @throws {TokenRefreshError} 서버 응답 code 가 0 이 아닐 때
 */
export async function validToken(): Promise<{ accTkn: string; rfrTkn: string }> {
  const accessToken  = tokenStore.getAccessToken();
  const refreshToken = tokenStore.getRefreshToken();

  // 액세스 토큰이 있으면 Authorization 헤더에 포함합니다.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const baseUrl = API_BASE.pst;
  const url     = buildUrl(baseUrl, '/login/storeValidToken');

  const res  = await fetch(url, {
    method:  'POST',
    headers,
    body:    JSON.stringify({data: { accTkn: accessToken, rfrTkn: refreshToken }}),
  });

  // console.log('res is :', res)

  // HTTP 레벨 오류 처리
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText, await res.text());
  }

  const json: ValidTokenResponse = await res.json();

  // console.log('json is :', json)

  // 비즈니스 레벨 오류 처리 — code 가 0 이 아니면 실패로 간주합니다.
  if (json.code !== 0) {
    throw new TokenRefreshError(json.code, json.message);
  }

  // 갱신된 토큰을 tokenStore 에 저장합니다.
  // console.log('token is :', json.data)
  const { accTkn, rfrTkn } = json.data;
  // console.log('accTkn is :', accTkn)
  // console.log('rfrTkn is :', rfrTkn)
  tokenStore.set(accTkn, rfrTkn);

  return { accTkn, rfrTkn };
}

// ── 공개 HTTP 메서드 ──────────────────────────────────────────────
/**
 * GET 요청을 보냅니다.
 *
 * @template T    - 응답 data 의 타입
 * @param base    - API_BASE 키 또는 직접 URL 문자열
 * @param path    - 엔드포인트 경로 (예: '/users/1')
 * @param options - query params, headers 등 (body 제외)
 */
export function get<T = unknown>(
  base:    ApiBase | string,
  path:    string,
  options?: Omit<RequestOptions, 'body'>,
) {
  // API_BASE 키이면 해당 URL로, 아니면 base 를 직접 URL로 사용합니다.
  const baseUrl = (API_BASE as Record<string, string>)[base] ?? base;
  return request<T>('GET', baseUrl, path, options);
}

/**
 * POST 요청을 보냅니다.
 *
 * @template T    - 응답 data 의 타입
 * @param base    - API_BASE 키 또는 직접 URL 문자열
 * @param path    - 엔드포인트 경로
 * @param body    - 요청 바디 (JSON 직렬화됨)
 * @param options - 추가 요청 옵션
 */
export function post<T = unknown>(
  base:    ApiBase | string,
  path:    string,
  body?:   unknown,
  options?: RequestOptions,
) {
  const baseUrl = (API_BASE as Record<string, string>)[base] ?? base;
  return request<T>('POST', baseUrl, path, { ...options, body: body !== undefined ? { data: body } : undefined });
}

/**
 * PUT 요청을 보냅니다. 리소스 전체를 교체할 때 사용합니다.
 *
 * @template T    - 응답 data 의 타입
 * @param base    - API_BASE 키 또는 직접 URL 문자열
 * @param path    - 엔드포인트 경로
 * @param body    - 요청 바디 (JSON 직렬화됨)
 * @param options - 추가 요청 옵션
 */
export function put<T = unknown>(
  base:    ApiBase | string,
  path:    string,
  body?:   unknown,
  options?: RequestOptions,
) {
  const baseUrl = (API_BASE as Record<string, string>)[base] ?? base;
  return request<T>('PUT', baseUrl, path, { ...options, body: body !== undefined ? { data: body } : undefined });
}

/**
 * PATCH 요청을 보냅니다. 리소스 일부를 수정할 때 사용합니다.
 *
 * @template T    - 응답 data 의 타입
 * @param base    - API_BASE 키 또는 직접 URL 문자열
 * @param path    - 엔드포인트 경로
 * @param body    - 요청 바디 (JSON 직렬화됨)
 * @param options - 추가 요청 옵션
 */
export function patch<T = unknown>(
  base:    ApiBase | string,
  path:    string,
  body?:   unknown,
  options?: RequestOptions,
) {
  const baseUrl = (API_BASE as Record<string, string>)[base] ?? base;
  return request<T>('PATCH', baseUrl, path, { ...options, body: body !== undefined ? { data: body } : undefined });
}

/**
 * DELETE 요청을 보냅니다.
 * (`delete` 는 예약어이므로 함수명을 `del` 로 사용합니다.)
 *
 * @template T    - 응답 data 의 타입
 * @param base    - API_BASE 키 또는 직접 URL 문자열
 * @param path    - 엔드포인트 경로
 * @param options - 추가 요청 옵션
 */
export function del<T = unknown>(
  base:    ApiBase | string,
  path:    string,
  options?: RequestOptions,
) {
  const baseUrl = (API_BASE as Record<string, string>)[base] ?? base;
  return request<T>('DELETE', baseUrl, path, options);
}


